import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  ClipboardList, ChevronDown, ChevronUp, Loader2, 
  UserX, CalendarPlus, Shield, Mail, Building, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_CONFIG = {
  trial_extended: { icon: CalendarPlus, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  trial_reduced: { icon: CalendarPlus, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  user_deleted: { icon: UserX, color: 'text-red-400', bg: 'bg-red-500/10' },
  user_updated: { icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  beta_invite_created: { icon: Mail, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  practice_approved: { icon: Building, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  practice_rejected: { icon: Building, color: 'text-red-400', bg: 'bg-red-500/10' },
  break_glass_access: { icon: Eye, color: 'text-red-400', bg: 'bg-red-500/10' },
};

const AdminAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded) fetchLogs();
  }, [expanded]);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-log`);
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-amber-400" />
            Admin Audit Log
            {logs.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded-full">{logs.length}</span>
            )}
          </CardTitle>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No admin actions logged yet.</p>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
              {logs.map(log => {
                const config = ACTION_CONFIG[log.action] || { icon: Shield, color: 'text-slate-400', bg: 'bg-slate-700' };
                const Icon = config.icon;
                const isBreakGlass = log.action === 'break_glass_access';
                return (
                  <div key={log.id}
                    className={`flex items-start gap-3 p-2.5 rounded-lg ${isBreakGlass ? 'bg-red-500/5 border border-red-500/20' : 'bg-slate-700/20'}`}
                    data-testid={`audit-${log.id}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${config.bg}`}>
                      <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white">{log.details}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-500">
                          by {log.admin_name} ({log.admin_role})
                        </span>
                        <span className="text-[10px] text-slate-600">{formatTime(log.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AdminAuditLog;
